import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface ThemeSettings {
  id?: string;
  themeName: string;
  colors: {
    primary: string;
    primaryHover: string;
    primaryLight: string;
    primaryDark: string;
    secondary: string;
    secondaryHover: string;
    secondaryLight: string;
    secondaryDark: string;
    accent: string;
    accentHover: string;
    accentLight: string;
    accentDark: string;
    background: string;
    backgroundSecondary: string;
    backgroundTertiary: string;
    textPrimary: string;
    textSecondary: string;
    textTertiary: string;
    textInverse: string;
    border: string;
    borderLight: string;
    borderDark: string;
    success: string;
    warning: string;
    error: string;
    info: string;
    headerBackground: string;
    headerText: string;
    footerBackground: string;
    footerText: string;
  };
  typography: {
    fontFamily: {
      primary: string;
      heading: string;
      monospace: string;
    };
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
      '4xl': string;
      '5xl': string;
      '6xl': string;
    };
    fontWeight: {
      light: string;
      normal: string;
      medium: string;
      semibold: string;
      bold: string;
      extrabold: string;
    };
  };
  layout: {
    borderRadius: {
      none: string;
      sm: string;
      base: string;
      md: string;
      lg: string;
      xl: string;
      '2xl': string;
      full: string;
    };
  };
}

const ThemeManagement: React.FC = () => {
  const [theme, setTheme] = useState<ThemeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'layout'>('colors');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/theme');
      if (response.data.success) {
        setTheme(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
      showMessage('error', 'Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSave = async () => {
    if (!theme) return;

    try {
      setSaving(true);
      const response = await api.put('/admin/theme', theme);
      if (response.data.success) {
        setTheme(response.data.data);
        showMessage('success', 'Theme settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      showMessage('error', 'Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset theme to defaults? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/admin/theme/reset');
      if (response.data.success) {
        setTheme(response.data.data);
        showMessage('success', 'Theme reset to defaults successfully!');
      }
    } catch (error) {
      console.error('Error resetting theme:', error);
      showMessage('error', 'Failed to reset theme');
    } finally {
      setSaving(false);
    }
  };

  const updateColor = (path: string, value: string) => {
    if (!theme) return;
    const newTheme = { ...theme };
    const keys = path.split('.');
    let obj: any = newTheme;
    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    setTheme(newTheme);
  };

  const updateTypography = (category: string, key: string, value: string) => {
    if (!theme) return;
    setTheme({
      ...theme,
      typography: {
        ...theme.typography,
        [category]: {
          ...theme.typography[category as keyof typeof theme.typography],
          [key]: value
        }
      }
    });
  };

  const updateLayout = (key: string, value: string) => {
    if (!theme) return;
    setTheme({
      ...theme,
      layout: {
        ...theme.layout,
        borderRadius: {
          ...theme.layout.borderRadius,
          [key]: value
        }
      }
    });
  };

  const ColorPicker = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded border cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 px-2 py-1 text-sm border rounded"
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading theme settings...</div>
      </div>
    );
  }

  if (!theme) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Failed to load theme settings</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Theme Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Customize colors, fonts, and layout for your website
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Theme Name */}
      <div className="p-6 bg-white border rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">Theme Name</label>
        <input
          type="text"
          value={theme.themeName}
          onChange={(e) => setTheme({ ...theme, themeName: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter theme name"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {(['colors', 'typography', 'layout'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Colors Tab */}
      {activeTab === 'colors' && (
        <div className="grid gap-6">
          {/* Primary Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Primary Brand Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Primary" value={theme.colors.primary} onChange={(v) => updateColor('colors.primary', v)} />
              <ColorPicker label="Primary Hover" value={theme.colors.primaryHover} onChange={(v) => updateColor('colors.primaryHover', v)} />
              <ColorPicker label="Primary Light" value={theme.colors.primaryLight} onChange={(v) => updateColor('colors.primaryLight', v)} />
              <ColorPicker label="Primary Dark" value={theme.colors.primaryDark} onChange={(v) => updateColor('colors.primaryDark', v)} />
            </div>
          </div>

          {/* Secondary Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Secondary Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Secondary" value={theme.colors.secondary} onChange={(v) => updateColor('colors.secondary', v)} />
              <ColorPicker label="Secondary Hover" value={theme.colors.secondaryHover} onChange={(v) => updateColor('colors.secondaryHover', v)} />
              <ColorPicker label="Secondary Light" value={theme.colors.secondaryLight} onChange={(v) => updateColor('colors.secondaryLight', v)} />
              <ColorPicker label="Secondary Dark" value={theme.colors.secondaryDark} onChange={(v) => updateColor('colors.secondaryDark', v)} />
            </div>
          </div>

          {/* Accent Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Accent Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Accent" value={theme.colors.accent} onChange={(v) => updateColor('colors.accent', v)} />
              <ColorPicker label="Accent Hover" value={theme.colors.accentHover} onChange={(v) => updateColor('colors.accentHover', v)} />
              <ColorPicker label="Accent Light" value={theme.colors.accentLight} onChange={(v) => updateColor('colors.accentLight', v)} />
              <ColorPicker label="Accent Dark" value={theme.colors.accentDark} onChange={(v) => updateColor('colors.accentDark', v)} />
            </div>
          </div>

          {/* Background Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Background Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Background" value={theme.colors.background} onChange={(v) => updateColor('colors.background', v)} />
              <ColorPicker label="Background Secondary" value={theme.colors.backgroundSecondary} onChange={(v) => updateColor('colors.backgroundSecondary', v)} />
              <ColorPicker label="Background Tertiary" value={theme.colors.backgroundTertiary} onChange={(v) => updateColor('colors.backgroundTertiary', v)} />
            </div>
          </div>

          {/* Text Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Text Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Text Primary" value={theme.colors.textPrimary} onChange={(v) => updateColor('colors.textPrimary', v)} />
              <ColorPicker label="Text Secondary" value={theme.colors.textSecondary} onChange={(v) => updateColor('colors.textSecondary', v)} />
              <ColorPicker label="Text Tertiary" value={theme.colors.textTertiary} onChange={(v) => updateColor('colors.textTertiary', v)} />
              <ColorPicker label="Text Inverse" value={theme.colors.textInverse} onChange={(v) => updateColor('colors.textInverse', v)} />
            </div>
          </div>

          {/* Border Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Border Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Border" value={theme.colors.border} onChange={(v) => updateColor('colors.border', v)} />
              <ColorPicker label="Border Light" value={theme.colors.borderLight} onChange={(v) => updateColor('colors.borderLight', v)} />
              <ColorPicker label="Border Dark" value={theme.colors.borderDark} onChange={(v) => updateColor('colors.borderDark', v)} />
            </div>
          </div>

          {/* Status Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Success" value={theme.colors.success} onChange={(v) => updateColor('colors.success', v)} />
              <ColorPicker label="Warning" value={theme.colors.warning} onChange={(v) => updateColor('colors.warning', v)} />
              <ColorPicker label="Error" value={theme.colors.error} onChange={(v) => updateColor('colors.error', v)} />
              <ColorPicker label="Info" value={theme.colors.info} onChange={(v) => updateColor('colors.info', v)} />
            </div>
          </div>

          {/* Header/Footer Colors */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Header & Footer Colors</h2>
            <div className="grid gap-3">
              <ColorPicker label="Header Background" value={theme.colors.headerBackground} onChange={(v) => updateColor('colors.headerBackground', v)} />
              <ColorPicker label="Header Text" value={theme.colors.headerText} onChange={(v) => updateColor('colors.headerText', v)} />
              <ColorPicker label="Footer Background" value={theme.colors.footerBackground} onChange={(v) => updateColor('colors.footerBackground', v)} />
              <ColorPicker label="Footer Text" value={theme.colors.footerText} onChange={(v) => updateColor('colors.footerText', v)} />
            </div>
          </div>
        </div>
      )}

      {/* Typography Tab */}
      {activeTab === 'typography' && (
        <div className="grid gap-6">
          {/* Font Families */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Font Families</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Font</label>
                <input
                  type="text"
                  value={theme.typography.fontFamily.primary}
                  onChange={(e) => updateTypography('fontFamily', 'primary', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., 'Inter', sans-serif"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Heading Font</label>
                <input
                  type="text"
                  value={theme.typography.fontFamily.heading}
                  onChange={(e) => updateTypography('fontFamily', 'heading', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., 'Poppins', sans-serif"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monospace Font</label>
                <input
                  type="text"
                  value={theme.typography.fontFamily.monospace}
                  onChange={(e) => updateTypography('fontFamily', 'monospace', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., 'Fira Code', monospace"
                />
              </div>
            </div>
          </div>

          {/* Font Sizes */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Font Sizes</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(theme.typography.fontSize).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">{key}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateTypography('fontSize', key, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="e.g., 1rem"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Font Weights */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Font Weights</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(theme.typography.fontWeight).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">{key}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateTypography('fontWeight', key, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="e.g., 400"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Layout Tab */}
      {activeTab === 'layout' && (
        <div className="grid gap-6">
          {/* Border Radius */}
          <div className="p-6 bg-white border rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Border Radius</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(theme.layout.borderRadius).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">{key}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateLayout(key, e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="e.g., 0.5rem"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeManagement;
